// A client's care plans and the goals inside each plan. Goals load with the plans and the active
// plan opens by itself, so the plan's substance is visible without clicking.
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import clsx from 'clsx';
import { Check, ChevronDown, ChevronRight, ClipboardList, Loader2, Pencil, Plus, Sparkles, Target, Trash2 } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useConfirm } from '../../components/ConfirmDialog';
import {
  Dialog, EmptyState, ErrorState, Field, FormRow, OverflowMenu, StatusPill, fmtDate, plural,
} from '../../components/ui';
import { useClientRecord, ClientFrame } from './ClientLayout';
import { codeLabel } from '../../utils/format';
import { dayOf, todayIso } from './clientShared';

// Care-plan statuses written for people, coloured by meaning.
const PLAN_STATUS = {
  active: { label: 'Active', tone: 'success' },
  review_due: { label: 'Review due', tone: 'warning' },
  completed: { label: 'Completed', tone: 'neutral' },
  inactive: { label: 'Inactive', tone: 'neutral' },
};
const GOAL_STATUS = {
  in_progress: { label: 'In progress', tone: 'info' },
  active: { label: 'In progress', tone: 'info' },
  not_started: { label: 'Not started', tone: 'neutral' },
  on_hold: { label: 'On hold', tone: 'warning' },
  completed: { label: 'Completed', tone: 'success' },
};
const PROGRESS_STEPS = [0, 25, 50, 75, 100];

const EMPTY_PLAN = { planName: '', description: '', startDate: '', reviewDate: '', status: 'active' };
const EMPTY_GOAL = { goalTitle: '', description: '', category: '', targetDate: '', progress: 0, status: 'in_progress', notes: '' };

function GoalRow({ goal, onEdit }) {
  const pct = Math.max(0, Math.min(100, Number(goal.progress) || 0));
  return (
    <li className="flex items-start gap-3 py-3">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-slate-800">{goal.goal_title}</span>
          <StatusPill status={goal.status || 'in_progress'} map={GOAL_STATUS} />
          {goal.category && <span className="badge-gray">{codeLabel(goal.category)}</span>}
        </div>
        {goal.description && <p className="text-[13px] text-slate-600 mt-1">{goal.description}</p>}
        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1.5">
          {goal.target_date && <span className="text-[12.5px] text-slate-500">Target {fmtDate(dayOf(goal.target_date))}</span>}
          <span className="flex items-center gap-2 min-w-[10rem] flex-1 max-w-xs">
            <span className="h-1.5 flex-1 rounded-full bg-slate-100 overflow-hidden" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label={`${goal.goal_title} progress`}>
              <span className="block h-full rounded-full bg-primary-500" style={{ width: `${pct}%` }} />
            </span>
            <span className="w-10 text-right text-[12.5px] font-medium text-slate-600 tabular-nums">{pct}%</span>
          </span>
        </div>
      </div>
      <button type="button" onClick={onEdit} aria-label={`Edit goal: ${goal.goal_title}`}
        className="inline-flex items-center justify-center w-9 h-9 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 shrink-0">
        <Pencil size={14} aria-hidden />
      </button>
    </li>
  );
}

export default function ClientCarePlan() {
  const { id } = useParams();
  const record = useClientRecord(id);
  const confirm = useConfirm();

  const [plans, setPlans] = useState([]);
  const [state, setState] = useState({ loading: true, error: null });
  const [expanded, setExpanded] = useState({});
  const [goalsByPlan, setGoals] = useState({});   // planId -> goals[] | 'error'

  const [planDialog, setPlanDialog] = useState(null); // { plan|null }
  const [planForm, setPlanForm] = useState(EMPTY_PLAN);
  const [goalDialog, setGoalDialog] = useState(null); // { planId, goal|null }
  const [goalForm, setGoalForm] = useState(EMPTY_GOAL);
  const [saving, setSaving] = useState(false);

  const [ai, setAi] = useState({ open: false, generating: false, plan: null, importing: false });

  const loadGoals = useCallback(async (planId) => {
    try {
      const r = await api.get(`/care-plans/${planId}`);
      setGoals((g) => ({ ...g, [planId]: r.data.goals || [] }));
    } catch { setGoals((g) => ({ ...g, [planId]: 'error' })); }
  }, []);

  const load = useCallback(async () => {
    setState({ loading: true, error: null });
    try {
      const r = await api.get(`/care-plans?client_id=${id}`);
      const list = Array.isArray(r.data) ? r.data : [];
      setPlans(list);
      setState({ loading: false, error: null });
      // Open the only plan, or the active ones; goals for every plan load with the list.
      const open = {};
      list.forEach((p) => { if (list.length === 1 || p.status === 'active') open[p.id] = true; });
      setExpanded(open);
      list.forEach((p) => loadGoals(p.id));
    } catch (e) {
      setState({ loading: false, error: e.response?.data?.message || 'We couldn’t load the care plans. Check your connection, then try again.' });
    }
  }, [id, loadGoals]);
  useEffect(() => { load(); }, [load]);

  const togglePlan = (planId) => {
    setExpanded((e) => ({ ...e, [planId]: !e[planId] }));
    if (!goalsByPlan[planId] || goalsByPlan[planId] === 'error') loadGoals(planId);
  };

  // ── Plans ──────────────────────────────────────────────────────────────────
  const openNewPlan = () => { setPlanForm(EMPTY_PLAN); setPlanDialog({ plan: null }); };
  const openEditPlan = (plan) => {
    setPlanForm({
      planName: plan.plan_name || '', description: plan.description || '',
      startDate: dayOf(plan.start_date), reviewDate: dayOf(plan.review_date), status: plan.status || 'active',
    });
    setPlanDialog({ plan });
  };

  async function savePlan(e) {
    if (e) e.preventDefault();
    if (!planForm.planName.trim()) return;
    setSaving(true);
    try {
      if (planDialog.plan) {
        const r = await api.put(`/care-plans/${planDialog.plan.id}`, planForm);
        setPlans((ps) => ps.map((p) => (p.id === planDialog.plan.id ? { ...p, ...r.data } : p)));
        toast.success('Care plan updated');
      } else {
        const r = await api.post('/care-plans', { ...planForm, clientId: id });
        setPlans((ps) => [{ ...r.data, goal_count: 0 }, ...ps]);
        setGoals((g) => ({ ...g, [r.data.id]: [] }));
        setExpanded((x) => ({ ...x, [r.data.id]: true }));
        toast.success('Care plan created');
      }
      setPlanDialog(null);
    } catch (err) { toast.error(err.response?.data?.message || 'Could not save the care plan'); }
    finally { setSaving(false); }
  }

  async function deletePlan(plan) {
    const n = Array.isArray(goalsByPlan[plan.id]) ? goalsByPlan[plan.id].length : Number(plan.goal_count) || 0;
    if (!await confirm(`“${plan.plan_name}” and its ${plural(n, 'goal')} are deleted. This can't be undone.`, {
      title: 'Delete this care plan?', danger: true, confirmLabel: 'Delete care plan',
    })) return;
    try {
      await api.delete(`/care-plans/${plan.id}`);
      setPlans((ps) => ps.filter((p) => p.id !== plan.id));
      toast.success('Care plan deleted');
    } catch (err) { toast.error(err.response?.data?.message || 'Could not delete the care plan'); }
  }

  // ── Goals ──────────────────────────────────────────────────────────────────
  const openNewGoal = (planId) => { setGoalForm(EMPTY_GOAL); setGoalDialog({ planId, goal: null }); };
  const openEditGoal = (goal, planId) => {
    setGoalForm({
      goalTitle: goal.goal_title || '', description: goal.description || '', category: goal.category || '',
      targetDate: dayOf(goal.target_date), progress: Number(goal.progress) || 0,
      status: goal.status === 'active' ? 'in_progress' : (goal.status || 'in_progress'), notes: goal.notes || '',
    });
    setGoalDialog({ planId, goal });
  };

  async function saveGoal(e) {
    if (e) e.preventDefault();
    if (!goalForm.goalTitle.trim()) return;
    const { planId, goal } = goalDialog;
    setSaving(true);
    try {
      if (goal) {
        const r = await api.put(`/care-plans/goals/${goal.id}`, goalForm);
        setGoals((g) => ({ ...g, [planId]: (Array.isArray(g[planId]) ? g[planId] : []).map((x) => (x.id === goal.id ? r.data : x)) }));
        toast.success('Goal updated');
      } else {
        const r = await api.post(`/care-plans/${planId}/goals`, goalForm);
        setGoals((g) => ({ ...g, [planId]: [...(Array.isArray(g[planId]) ? g[planId] : []), r.data] }));
        setPlans((ps) => ps.map((p) => (p.id === planId ? { ...p, goal_count: (Number(p.goal_count) || 0) + 1 } : p)));
        toast.success('Goal added');
      }
      setGoalDialog(null);
    } catch (err) { toast.error(err.response?.data?.message || 'Could not save the goal'); }
    finally { setSaving(false); }
  }

  // ── AI draft (reviewed before anything is saved) ───────────────────────────
  const generateAiPlan = async () => {
    setAi({ open: true, generating: true, plan: null, importing: false });
    try {
      const { data } = await api.post('/ai/generate-care-plan', { clientId: id });
      setAi({ open: true, generating: false, plan: data.plan, importing: false });
    } catch (err) {
      toast.error(err.response?.data?.message || 'The AI draft failed. Try again.');
      setAi({ open: false, generating: false, plan: null, importing: false });
    }
  };

  const importAiPlan = async () => {
    const aiPlan = ai.plan;
    if (!aiPlan) return;
    setAi((s) => ({ ...s, importing: true }));
    try {
      const today = new Date();
      const reviewDate = aiPlan.reviewDate || new Date(today.setFullYear(today.getFullYear() + 1)).toISOString().split('T')[0];
      const { data: newPlan } = await api.post('/care-plans', {
        clientId: id, planName: aiPlan.planName, description: aiPlan.description,
        startDate: todayIso(), reviewDate, status: 'active',
      });
      for (const g of (aiPlan.goals || [])) {
        await api.post(`/care-plans/${newPlan.id}/goals`, {
          goalTitle: g.goalTitle, description: g.description, category: g.category,
          targetDate: g.targetDate, status: g.status || 'in_progress', progress: 0,
        });
      }
      toast.success(`Care plan created with ${plural(aiPlan.goals?.length || 0, 'goal')}`);
      setAi({ open: false, generating: false, plan: null, importing: false });
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not import the care plan');
      setAi((s) => ({ ...s, importing: false }));
    }
  };
  const closeAi = () => setAi({ open: false, generating: false, plan: null, importing: false });

  const setP = (k, v) => setPlanForm((f) => ({ ...f, [k]: v }));
  const setG = (k, v) => setGoalForm((f) => ({ ...f, [k]: v }));

  return (
    <ClientFrame record={record}>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-slate-900">Care plans</h2>
          <p className="text-[13px] text-slate-500 mt-0.5">
            {state.loading ? 'Loading…' : plural(plans.length, 'plan', 'plans', 'No plans')} on file. Care-plan goals track progress against a plan.
            {' '}The participant’s NDIS goal register is on the <Link to={`/clients/${id}/goals`} className="text-primary-700 hover:underline">Goals tab</Link>.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button type="button" onClick={generateAiPlan} disabled={ai.generating} className="btn-secondary">
            {ai.generating ? <Loader2 size={14} className="animate-spin" aria-hidden /> : <Sparkles size={14} className="text-violet-500" aria-hidden />}
            Draft with AI
          </button>
          <button type="button" onClick={openNewPlan} className="btn-primary"><Plus size={15} aria-hidden /> New care plan</button>
        </div>
      </div>

      {state.loading ? (
        <div className="space-y-3">
          {[0, 1].map((i) => <div key={i} className="bg-white border border-slate-200 rounded-xl p-5 space-y-3"><div className="skeleton h-5 w-56" /><div className="skeleton h-4" /><div className="skeleton h-4 w-2/3" /></div>)}
        </div>
      ) : state.error ? (
        <div className="bg-white border border-slate-200 rounded-xl"><ErrorState message={state.error} onRetry={load} /></div>
      ) : plans.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl">
          <EmptyState icon={ClipboardList} title="No care plan yet"
            hint="Create one, or let AI draft one from this client’s profile and documents. You review the draft before anything is saved."
            action={(
              <div className="flex flex-wrap justify-center gap-2">
                <button type="button" onClick={generateAiPlan} className="btn-secondary"><Sparkles size={14} className="text-violet-500" aria-hidden /> Draft with AI</button>
                <button type="button" onClick={openNewPlan} className="btn-primary"><Plus size={15} aria-hidden /> New care plan</button>
              </div>
            )} />
        </div>
      ) : (
        <ul className="space-y-3">
          {plans.map((plan) => {
            const open = !!expanded[plan.id];
            const goals = goalsByPlan[plan.id];
            const count = Array.isArray(goals) ? goals.length : Number(plan.goal_count) || 0;
            const panelId = `plan-panel-${plan.id}`;
            return (
              <li key={plan.id} className="bg-white border border-slate-200 rounded-xl">
                <div className="flex items-start gap-2 px-3 sm:px-4 py-3">
                  <button type="button" onClick={() => togglePlan(plan.id)} aria-expanded={open} aria-controls={panelId}
                    className="flex flex-1 min-w-0 items-start gap-2.5 rounded-lg px-1 py-1 text-left hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400">
                    {open ? <ChevronDown size={16} className="mt-0.5 text-slate-400 shrink-0" aria-hidden /> : <ChevronRight size={16} className="mt-0.5 text-slate-400 shrink-0" aria-hidden />}
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-slate-800 text-sm">{plan.plan_name}</span>
                        <StatusPill status={plan.status || 'active'} map={PLAN_STATUS} />
                      </span>
                      {plan.description && <span className="block text-[13px] text-slate-500 mt-0.5 truncate">{plan.description}</span>}
                      <span className="block text-[12.5px] text-slate-500 mt-1 tabular-nums">
                        {[plan.review_date ? `Review ${fmtDate(dayOf(plan.review_date))}` : null, plural(count, 'goal')].filter(Boolean).join(' · ')}
                      </span>
                    </span>
                  </button>
                  <OverflowMenu label={`Actions for ${plan.plan_name}`} className="mt-0.5 !p-2" items={[
                    { label: 'Add goal', icon: Target, onClick: () => { setExpanded((x) => ({ ...x, [plan.id]: true })); openNewGoal(plan.id); } },
                    { label: 'Edit plan', icon: Pencil, onClick: () => openEditPlan(plan) },
                    { divider: true },
                    { label: 'Delete plan', icon: Trash2, danger: true, onClick: () => deletePlan(plan) },
                  ]} />
                </div>

                {open && (
                  <div id={panelId} className="border-t border-slate-100 px-4 sm:px-5 pb-3">
                    <div className="flex items-center justify-between pt-3">
                      <h3 className="text-[13px] font-semibold text-slate-700">Goals</h3>
                      <button type="button" onClick={() => openNewGoal(plan.id)}
                        className="inline-flex items-center gap-1.5 h-9 px-2.5 rounded-lg text-[13px] font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100">
                        <Plus size={14} aria-hidden /> Add
                      </button>
                    </div>
                    {goals === undefined ? (
                      <div className="space-y-2 py-2" aria-busy="true">{[0, 1].map((i) => <div key={i} className="skeleton h-10" />)}</div>
                    ) : goals === 'error' ? (
                      <ErrorState message="We couldn’t load this plan’s goals." onRetry={() => loadGoals(plan.id)} />
                    ) : goals.length === 0 ? (
                      <p className="text-sm text-slate-500 py-2">No goals in this plan yet. Add what the participant wants to achieve, so progress can be tracked.</p>
                    ) : (
                      <ul className="divide-y divide-slate-100">
                        {goals.map((g) => <GoalRow key={g.id} goal={g} onEdit={() => openEditGoal(g, plan.id)} />)}
                      </ul>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {/* Plan dialog */}
      <Dialog open={!!planDialog} onClose={() => setPlanDialog(null)} title={planDialog?.plan ? 'Edit care plan' : 'New care plan'}
        footer={<>
          <button type="button" onClick={() => setPlanDialog(null)} className="btn-secondary">Cancel</button>
          <button type="submit" form="plan-form" disabled={saving || !planForm.planName.trim()} className="btn-primary">
            {saving ? 'Saving…' : planDialog?.plan ? 'Save changes' : 'Create plan'}
          </button>
        </>}>
        <form id="plan-form" onSubmit={savePlan} className="space-y-4">
          <Field label="Plan name" required>{(f) => <input id={f} className="input-field" required value={planForm.planName} onChange={(e) => setP('planName', e.target.value)} placeholder="For example: NDIS annual support plan" />}</Field>
          <Field label="Description">{(f) => <textarea id={f} className="input-field" rows={3} value={planForm.description} onChange={(e) => setP('description', e.target.value)} placeholder="A short overview of this plan" />}</Field>
          <FormRow cols={2}>
            <Field label="Start date">{(f) => <input id={f} type="date" className="input-field" value={planForm.startDate} onChange={(e) => setP('startDate', e.target.value)} />}</Field>
            <Field label="Review date">{(f) => <input id={f} type="date" className="input-field" value={planForm.reviewDate} onChange={(e) => setP('reviewDate', e.target.value)} />}</Field>
          </FormRow>
          <Field label="Status">
            {(f) => (
              <select id={f} className="input-field" value={planForm.status} onChange={(e) => setP('status', e.target.value)}>
                <option value="active">Active</option>
                <option value="review_due">Review due</option>
                <option value="completed">Completed</option>
                <option value="inactive">Inactive</option>
              </select>
            )}
          </Field>
        </form>
      </Dialog>

      {/* Goal dialog */}
      <Dialog open={!!goalDialog} onClose={() => setGoalDialog(null)} title={goalDialog?.goal ? 'Edit goal' : 'Add goal'}
        footer={<>
          <button type="button" onClick={() => setGoalDialog(null)} className="btn-secondary">Cancel</button>
          <button type="submit" form="goal-form" disabled={saving || !goalForm.goalTitle.trim()} className="btn-primary">
            {saving ? 'Saving…' : goalDialog?.goal ? 'Save goal' : 'Add goal'}
          </button>
        </>}>
        <form id="goal-form" onSubmit={saveGoal} className="space-y-4">
          <Field label="Goal" required>{(f) => <input id={f} className="input-field" required value={goalForm.goalTitle} onChange={(e) => setG('goalTitle', e.target.value)} placeholder="For example: Improve daily living skills" />}</Field>
          <Field label="Description">{(f) => <textarea id={f} className="input-field" rows={3} value={goalForm.description} onChange={(e) => setG('description', e.target.value)} />}</Field>
          <FormRow cols={2}>
            <Field label="Category">{(f) => <input id={f} className="input-field" value={goalForm.category} placeholder="For example: Social, Health" onChange={(e) => setG('category', e.target.value)} />}</Field>
            <Field label="Target date">{(f) => <input id={f} type="date" className="input-field" value={goalForm.targetDate} onChange={(e) => setG('targetDate', e.target.value)} />}</Field>
          </FormRow>
          <Field label="Status">
            {(f) => (
              <select id={f} className="input-field" value={goalForm.status} onChange={(e) => setG('status', e.target.value)}>
                <option value="in_progress">In progress</option>
                <option value="not_started">Not started</option>
                <option value="on_hold">On hold</option>
                <option value="completed">Completed</option>
              </select>
            )}
          </Field>
          <fieldset>
            <legend className="text-[13px] font-medium text-slate-700 mb-1">Progress</legend>
            <div className="inline-flex flex-wrap rounded-lg bg-slate-100 p-0.5" role="radiogroup" aria-label="Progress">
              {PROGRESS_STEPS.map((p) => {
                const on = Number(goalForm.progress) === p;
                return (
                  <button key={p} type="button" role="radio" aria-checked={on} onClick={() => setG('progress', p)}
                    className={clsx('h-8 min-w-[3.5rem] px-3 rounded-md text-[13px] font-medium tabular-nums transition-colors duration-150',
                      on ? 'bg-white text-slate-900 shadow-[0_1px_2px_rgba(16,24,40,.08)]' : 'text-slate-600 hover:text-slate-900')}>
                    {p}%
                  </button>
                );
              })}
            </div>
            {!PROGRESS_STEPS.includes(Number(goalForm.progress)) && (
              <p className="text-[12px] text-slate-500 mt-1">Currently {Number(goalForm.progress) || 0}%. Pick a step to change it.</p>
            )}
          </fieldset>
          <Field label="Notes">{(f) => <textarea id={f} className="input-field" rows={2} value={goalForm.notes} onChange={(e) => setG('notes', e.target.value)} />}</Field>
        </form>
      </Dialog>

      {/* AI draft preview */}
      <Dialog open={ai.open} onClose={closeAi} size="lg" dismissable={!ai.importing}
        title="AI-drafted care plan" description="Review the draft. Nothing is saved until you import it."
        footer={!ai.generating && ai.plan ? (<>
          <button type="button" onClick={closeAi} className="btn-secondary" disabled={ai.importing}>Discard</button>
          <button type="button" onClick={generateAiPlan} className="btn-secondary" disabled={ai.importing}><Sparkles size={13} className="text-violet-500" aria-hidden /> Draft again</button>
          <button type="button" onClick={importAiPlan} disabled={ai.importing} className="btn-primary">
            {ai.importing ? <Loader2 size={14} className="animate-spin" aria-hidden /> : <Check size={14} aria-hidden />}
            {ai.importing ? 'Importing…' : 'Import plan'}
          </button>
        </>) : null}>
        {ai.generating && (
          <div className="flex items-center justify-center gap-2 py-10 text-slate-500">
            <Loader2 size={18} className="animate-spin" aria-hidden /><span className="text-sm">Drafting a care plan for this participant…</span>
          </div>
        )}
        {!ai.generating && ai.plan && (
          <div className="space-y-4">
            <div className="rounded-xl border border-slate-200 p-4">
              <p className="font-semibold text-slate-900 text-sm">{ai.plan.planName}</p>
              {ai.plan.description && <p className="text-[13px] text-slate-600 mt-1">{ai.plan.description}</p>}
              {ai.plan.reviewDate && <p className="text-[12.5px] text-slate-500 mt-1">Review {fmtDate(ai.plan.reviewDate)}</p>}
            </div>
            <div>
              <p className="text-[13px] font-semibold text-slate-700 mb-2">{plural(ai.plan.goals?.length || 0, 'goal')}</p>
              <ul className="space-y-2">
                {(ai.plan.goals || []).map((g, i) => (
                  <li key={i} className="rounded-xl border border-slate-200 p-4">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="text-sm font-semibold text-slate-800">{g.goalTitle}</span>
                      {g.category && <span className="badge-gray">{codeLabel(g.category)}</span>}
                    </div>
                    {g.description && <p className="text-[13px] text-slate-600 leading-relaxed">{g.description}</p>}
                    {g.targetDate && <p className="text-[12.5px] text-slate-500 mt-1">Target {fmtDate(g.targetDate)}</p>}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </Dialog>
    </ClientFrame>
  );
}
